// ============================================================
// MarkdownNote (v8.363.10) - a handout note, rendered from the AST.
// No raw HTML ever reaches the DOM: the parser only emits text, emphasis,
// code and links, and a link opens in a new tab with rel="noopener".
// ============================================================
import { useMemo } from 'react';
import { parseMarkdown, type Inline } from '../utils/miniMarkdown';
import styles from '../styles/HandoutsDrawer.module.css';

function Inlines({ inlines }: { inlines: Inline[] }) {
  return (
    <>
      {inlines.map((n, i) => {
        switch (n.t) {
          case 'bold':
            return <strong key={i}>{n.v}</strong>;
          case 'italic':
            return <em key={i}>{n.v}</em>;
          case 'code':
            return <code key={i}>{n.v}</code>;
          case 'link':
            return (
              <a key={i} href={n.href} target="_blank" rel="noopener noreferrer">
                {n.v}
              </a>
            );
          default:
            return <span key={i}>{n.v}</span>;
        }
      })}
    </>
  );
}

export default function MarkdownNote({ source, testId = 'handout-note' }: { source: string; testId?: string }) {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  return (
    <div className={styles.note} data-testid={testId}>
      {blocks.map((b, i) => {
        if (b.t === 'heading') {
          const H = `h${b.level}` as 'h1' | 'h2' | 'h3';
          return (
            <H key={i}>
              <Inlines inlines={b.inlines} />
            </H>
          );
        }
        if (b.t === 'list') {
          const L = b.ordered ? 'ol' : 'ul';
          return (
            <L key={i}>
              {b.items.map((it, j) => (
                <li key={j}>
                  <Inlines inlines={it} />
                </li>
              ))}
            </L>
          );
        }
        return (
          <p key={i}>
            <Inlines inlines={b.inlines} />
          </p>
        );
      })}
    </div>
  );
}
